/**
 * Sub-component (not a routed page): create/display/edit form for AI training data
 * sets, discriminated by type ("search" or "submission_verification"). Scrapes a
 * product URL for HTML + extracted data, shows the raw HTML beside a ground-truth
 * form (SubmissionVerificationExtractedDataForm or SearchExtractedDataForm), and
 * uploads/edits/deletes sets via api/aiTrainingData (TrainingDataAPI). Used by the
 * "add training data" tabs and by AdminListViewContainer's detail view.
 */
import { useImmer } from "use-immer";
import React, { useState } from "react";
import InputField, { FieldConfig } from "@/components/InputField";
import LoadingSpinner from "@/components/LoadingSpinner";
import { Link2 } from "lucide-react";
import {
    SubmissionVerificationExtractedData,
    SubmissionVerificationTrainingDataSetDisplay,
    SubmissionVerificationTrainingDataSetUpload,
    SearchTrainingDataSetDisplay,
    SearchTrainingDataSetUpload,
    SearchResultsExtractedData,
    SearchSingleResultExtractedData,
} from "@/types/trainingData";
import { Trash2 } from "lucide-react";
import AlertDialog from "@/components/AlertDialog";
import SubmissionVerificationExtractedDataForm from "./SubmissionVerification/SubmissionVerificationExtractedDataForm";
import SearchGroundTruthForm from "@/pages/admin/ai/Search/SearchExtractedDataForm";
import { TrainingDataAPI } from "@/api/aiTrainingData";
import { formatItemLink } from "@/utils/submission";
import { extractDomain } from "@/utils/url";

export type CreateDisplayTrainingDataMode = "create" | "display" | "edit";
export type CreateDisplayTrainingDataType = "search" | "submission_verification";

export interface SubmissionVerificationTrainingDataCreateEditProps {
    mode: CreateDisplayTrainingDataMode;
    type: "submission_verification";
    trainingData?: SubmissionVerificationTrainingDataSetDisplay;
    onDelete?: () => void;
}

export interface SearchTrainingDataCreateDisplayProps {
    mode: CreateDisplayTrainingDataMode;
    type: "search";
    trainingData?: SearchTrainingDataSetDisplay;
    onDelete?: () => void;
}

export type CreateDisplayTrainingDataProps =
    | SubmissionVerificationTrainingDataCreateEditProps
    | SearchTrainingDataCreateDisplayProps;

const TrainingDataCreateDisplay: React.FC<CreateDisplayTrainingDataProps> = ({
    mode,
    type,
    trainingData,
    onDelete,
}) => {
    if (mode !== "create" && !trainingData) {
        throw new Error("Training data is required when mode is not 'create'");
    }

    const [isScraping, setIsScraping] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [urlError, setUrlError] = useState<string | null>(null);
    const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
    const [isUpdateAlertOpen, setIsUpdateAlertOpen] = useState(false);
    const [selectionStart, setSelectionStart] = useState<number | null>(null);
    const [selectionEnd, setSelectionEnd] = useState<number | null>(null);
    // State for both types, initialized from props
    const [label, setLabel] = useState(() => {
        if (mode !== "create" && trainingData) {
            return trainingData.label;
        }
        return "";
    });

    // Store original label for deletion/editing
    const originalLabel = mode !== "create" && trainingData ? trainingData.label : "";

    const [productUrl, setProductUrl] = useState(() => {
        if (type === "submission_verification" && mode !== "create" && trainingData) {
            return (trainingData as SubmissionVerificationTrainingDataSetDisplay).extractedData
                .productUrl;
        }
        return "";
    });

    const [html, setHtml] = useState(() => {
        if (mode !== "create" && trainingData) {
            return trainingData.html;
        }
        return null;
    });

    // State for Submission Verification
    const [submissionVerificationExtractedData, setSubmissionVerificationExtractedData] =
        useImmer<SubmissionVerificationExtractedData | null>(() => {
            if (type === "submission_verification") {
                if (mode !== "create" && trainingData) {
                    return (trainingData as SubmissionVerificationTrainingDataSetDisplay)
                        .extractedData;
                } else if (mode === "create") {
                    return {
                        title: null,
                        description: null,
                        price: null,
                        available: null,
                        imageUrls: null,
                        isProductPage: false,
                        productUrl: "",
                    };
                }
            }
            return null;
        });

    // State for AI Search
    const [searchResultsExtractedData, setSearchResultsExtractedData] =
        useImmer<SearchResultsExtractedData | null>(() => {
            if (type === "search") {
                if (mode !== "create" && trainingData) {
                    return (trainingData as SearchTrainingDataSetDisplay).extractedData;
                } else if (mode === "create") {
                    return {
                        resultsUrl: "",
                        results: [
                            {
                                title: "",
                                description: "",
                                size: "",
                                price: "0.00",
                                imageUrl: "",
                                productUrl: "",
                            },
                        ],
                    };
                }
            }
            return null;
        });

    const labelFieldConfig: FieldConfig = {
        key: "label",
        label: "Label",
        type: "text",
        readonly: mode !== "create",
        fieldState: label,
        setFieldState: setLabel,
        placeholder: "enter label for this training data",
    };

    const getDisplayedHtml = () => {
        return html; // always show raw HTML
    };

    const htmlFieldConfig: FieldConfig = {
        key: "htmlContent",
        label: "HTML Content",
        type: "textarea",
        readonly: true,
        fieldState: getDisplayedHtml(),
        setFieldState: () => {}, // No-op function since field is readonly
        placeholder: "HTML content will be populated via scraping",
    };

    const handleDelete = () => {
        setIsDeleteAlertOpen(true);
    };

    const confirmDelete = async () => {
        setIsDeleteAlertOpen(false);
        try {
            if (type === "search") {
                await TrainingDataAPI.deleteSearchTrainingDataSet(originalLabel);
            } else {
                await TrainingDataAPI.deleteSubmissionVerificationTrainingDataSet(originalLabel);
            }
            onDelete?.();
        } catch (error) {
            console.error("Failed to delete training data:", error);
        }
    };

    const cancelDelete = () => {
        setIsDeleteAlertOpen(false);
    };

    const confirmUpdate = async () => {
        setIsUpdateAlertOpen(false);
        setIsSubmitting(true);
        try {
            await TrainingDataAPI.editSubmissionVerificationTrainingDataSetExtractedData(
                originalLabel,
                submissionVerificationExtractedData as SubmissionVerificationExtractedData
            );
            alert("Training data edited successfully!");
        } catch (error) {
            console.error("Failed to update training data:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const cancelUpdate = () => {
        setIsUpdateAlertOpen(false);
    };

    const validateAndFormatUrl = (url: string): string | null => {
        try {
            const formatted = formatItemLink(url);
            new URL(formatted);
            setProductUrl(formatted);
            setUrlError(null);

            // Extract domain and set label
            try {
                const safeDomain = extractDomain(formatted);
                setLabel(`${safeDomain}-`);
            } catch {
                // If domain extraction fails, don't set a label
            }

            return formatted;
        } catch (e) {
            const errorMsg = e instanceof Error ? e.message : String(e);
            setUrlError(errorMsg);
            return null;
        }
    };

    const handleScrape = async () => {
        if (!productUrl) {
            alert("Please enter a URL to scrape.");
            return;
        }

        const formattedUrl = validateAndFormatUrl(productUrl);
        if (!formattedUrl) {
            alert(
                "The URL you entered is not valid. Please enter a valid URL (e.g., https://example.com/item).\nError: " +
                    urlError
            );
            return;
        }
        setIsScraping(true);
        try {
            const data = await TrainingDataAPI.scrapeTrainingDataUrl(formattedUrl, type);
            setHtml(data.html);
            if (type === "submission_verification" && data.extractedData) {
                const extractedData = data.extractedData as SubmissionVerificationExtractedData;
                // Normalize the data to ensure available is always a boolean, not null
                console.log("extractedData", extractedData);
                setSubmissionVerificationExtractedData(extractedData);
            } else if (type === "search" && data.extractedData) {
                const extractedData = data.extractedData as SearchResultsExtractedData;
                console.log("extractedData", extractedData);
                setSearchResultsExtractedData(extractedData);
            }
        } catch (error: any) {
            let errorMsg = "An error occurred while scraping the item URL.";
            if (error?.response?.data?.error) {
                errorMsg += `\nDetails: ${error.response.data.error}`;
            } else if (error?.message) {
                errorMsg += `\nDetails: ${error.message}`;
            } else if (typeof error === "string") {
                errorMsg += `\nDetails: ${error}`;
            }
            alert(errorMsg);
            console.error("Error scraping item data", error);
        } finally {
            setIsScraping(false);
        }
    };

    const clearForm = () => {
        setLabel("");
        setProductUrl("");
        setHtml("");
        setUrlError(null);

        // Reset ground truth based on the type
        if (type === "submission_verification") {
            setSubmissionVerificationExtractedData({
                title: null,
                description: null,
                price: null,
                available: null,
                imageUrls: null,
                isProductPage: false,
                productUrl: "",
            });
        } else if (type === "search") {
            setSearchResultsExtractedData({
                resultsUrl: "",
                results: [
                    {
                        title: "",
                        description: "",
                        size: "",
                        price: "0.00",
                        imageUrl: "",
                        productUrl: "",
                    },
                ],
            });
        }
    };

    const handleSubmit = async () => {
        if (!label.trim()) {
            alert("Label is required.");
            return;
        }

        if (type === "submission_verification") {
            if (!submissionVerificationExtractedData) return;

            // Validation based on isProductPage status
            if (!submissionVerificationExtractedData.isProductPage) {
                // When not a product page, ensure all fields are null
                if (
                    submissionVerificationExtractedData.title !== null ||
                    submissionVerificationExtractedData.description !== null ||
                    submissionVerificationExtractedData.price !== null ||
                    submissionVerificationExtractedData.available !== null ||
                    submissionVerificationExtractedData.imageUrls !== null
                ) {
                    alert("When not a product page, all product fields must be empty (null).");
                    return;
                }
            } else {
                // When it is a product page
                if (submissionVerificationExtractedData.available) {
                    // All fields must be filled
                    if (
                        !submissionVerificationExtractedData.title ||
                        !submissionVerificationExtractedData.title.trim()
                    ) {
                        alert("Ground Truth Title is required when product page is available.");
                        return;
                    }
                    if (
                        !submissionVerificationExtractedData.description ||
                        !submissionVerificationExtractedData.description.trim()
                    ) {
                        alert(
                            "Ground Truth Description is required when product page is available."
                        );
                        return;
                    }
                    if (
                        submissionVerificationExtractedData.price === null ||
                        isNaN(Number(submissionVerificationExtractedData.price)) ||
                        Number(submissionVerificationExtractedData.price) < 0
                    ) {
                        alert(
                            "Ground Truth Price must be a valid non-negative number when product page is available."
                        );
                        return;
                    }
                    if (
                        !submissionVerificationExtractedData.imageUrls ||
                        submissionVerificationExtractedData.imageUrls.length === 0
                    ) {
                        alert("At least one image URL is required when product page is available.");
                        return;
                    }
                    // Check that there's at least one non-empty image URL
                    const nonEmptyImageUrls = submissionVerificationExtractedData.imageUrls.filter(
                        (url) => url.trim() !== ""
                    );
                    if (nonEmptyImageUrls.length === 0) {
                        alert("At least one image URL is required when product page is available.");
                        return;
                    }
                    // Validate image URLs
                    for (let i = 0; i < submissionVerificationExtractedData.imageUrls.length; i++) {
                        const url = submissionVerificationExtractedData.imageUrls[i].trim();
                        if (url) {
                            try {
                                new URL(url);
                            } catch (e) {
                                alert(`Image URL #${i + 1} is not a valid URL. Please correct it.`);
                                return;
                            }
                        }
                    }
                } else {
                    // When product page but not available, only validate filled fields
                    if (
                        submissionVerificationExtractedData.imageUrls &&
                        submissionVerificationExtractedData.imageUrls.length > 0
                    ) {
                        for (
                            let i = 0;
                            i < submissionVerificationExtractedData.imageUrls.length;
                            i++
                        ) {
                            const url = submissionVerificationExtractedData.imageUrls[i].trim();
                            if (url) {
                                try {
                                    new URL(url);
                                } catch (e) {
                                    alert(
                                        `Image URL #${i + 1} is not a valid URL. Please correct it.`
                                    );
                                    return;
                                }
                            }
                        }
                    }
                }
            }
        } else if (type === "search") {
            if (!searchResultsExtractedData) return;

            if (!searchResultsExtractedData.resultsUrl.trim()) {
                alert("Results URL is required.");
                return;
            }
            try {
                new URL(searchResultsExtractedData.resultsUrl);
            } catch (e) {
                alert("Results URL is not a valid URL. Please correct it.");
                return;
            }

            for (let i = 0; i < searchResultsExtractedData.results.length; i++) {
                const result = searchResultsExtractedData.results[i];
                if (!result.title.trim()) {
                    alert(`Result #${i + 1} title is required.`);
                    return;
                }
                if (
                    !result.price ||
                    result.price.trim() === "" ||
                    isNaN(parseFloat(result.price)) ||
                    parseFloat(result.price) < 0
                ) {
                    alert(`Result #${i + 1} price must be a valid non-negative number.`);
                    return;
                }
                if (result.imageUrl && result.imageUrl.trim()) {
                    try {
                        new URL(result.imageUrl);
                    } catch (e) {
                        alert(`Result #${i + 1} image URL is not a valid URL. Please correct it.`);
                        return;
                    }
                }
                if (!result.productUrl.trim()) {
                    alert(`Result #${i + 1} item URL is required.`);
                    return;
                }
                try {
                    new URL(result.productUrl);
                } catch (e) {
                    alert(`Result #${i + 1} item URL is not a valid URL. Please correct it.`);
                    return;
                }
            }
        }

        setIsSubmitting(true);
        try {
            const processedLabel = label.trim().replace(/\s+/g, "_");

            if (type === "submission_verification" && submissionVerificationExtractedData) {
                // Filter out empty image URLs, and convert empty array to null
                let processedImageUrls = null;
                if (submissionVerificationExtractedData.imageUrls) {
                    const filtered = submissionVerificationExtractedData.imageUrls.filter(
                        (url) => url.trim() !== ""
                    );
                    processedImageUrls = filtered.length > 0 ? filtered : null;
                }

                // Format and validate productUrl
                const formattedProductUrl = validateAndFormatUrl(productUrl.trim());
                if (!formattedProductUrl) {
                    alert(`Invalid product URL: ${urlError}`);
                    return;
                }

                const finalExtractedData = {
                    ...submissionVerificationExtractedData,
                    price: submissionVerificationExtractedData.price,
                    imageUrls: processedImageUrls,
                    productUrl: formattedProductUrl,
                };

                if (mode === "edit") {
                    setIsUpdateAlertOpen(true);
                } else {
                    // Create new training data set
                    const trainingDataSet: SubmissionVerificationTrainingDataSetUpload = {
                        label: processedLabel,
                        html: html || "",
                        extractedData: finalExtractedData,
                    };

                    await TrainingDataAPI.uploadSubmissionVerificationTrainingDataSet(
                        trainingDataSet
                    );
                }
            } else if (type === "search" && searchResultsExtractedData) {
                const finalExtractedData = {
                    ...searchResultsExtractedData,
                    results: searchResultsExtractedData.results.map(
                        (result: SearchSingleResultExtractedData) => ({
                            ...result,
                            // Ensure price is formatted with 2 decimal places
                            price: parseFloat(result.price).toFixed(2),
                            description: result.description?.trim() || undefined,
                            size: result.size?.trim() || undefined,
                            imageUrl: result.imageUrl?.trim() || "",
                        })
                    ),
                };

                const trainingDataSet: SearchTrainingDataSetUpload = {
                    label: processedLabel,
                    html: html || "",
                    extractedData: finalExtractedData,
                };

                await TrainingDataAPI.uploadSearchTrainingDataSet(trainingDataSet);
            }

            if (mode === "create") clearForm();
        } catch (error) {
            console.error("Upload error", error);
            alert("Upload error: " + error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="flex flex-col h-full min-h-0 w-full p-4">
            {mode !== "create" && (
                <button
                    onClick={handleDelete}
                    className="absolute top-[-20px] right-[10px] p-2 text-red-600 hover:text-red-800"
                >
                    <Trash2 className="w-6 h-6 mt-2" />
                </button>
            )}
            <div className="flex-none mb-4">
                <InputField fieldConfig={labelFieldConfig} />
            </div>
            <div className="flex-none flex items-center space-x-2 mb-4">
                {productUrl && (
                    <a
                        href={productUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-10 h-10 text-slate-600 hover:text-slate-800 flex-shrink-0"
                        title="Open URL in new tab"
                    >
                        <Link2 className="w-5 h-5" />
                    </a>
                )}
                <input
                    id="url"
                    type="text"
                    value={productUrl}
                    onChange={(e) => {
                        setProductUrl(e.target.value);
                        if (urlError) setUrlError(null);
                    }}
                    placeholder="enter url to scrape for html"
                    readOnly={mode !== "create"}
                    className={`flex-1 px-2 py-2 border rounded-lg focus:border-black focus:outline-none tracking-wider text-sm h-10 ${
                        mode !== "create" ? "bg-gray-100 cursor-not-allowed" : ""
                    } ${urlError ? "border-red-500" : ""}`}
                />
                {mode === "create" && (
                    <button
                        onClick={handleScrape}
                        className="relative w-24 h-10 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-700 flex-shrink-0 flex items-center justify-center"
                        disabled={isScraping}
                    >
                        {isScraping ? <LoadingSpinner className="text-white w-6 h-6" /> : "scrape"}
                    </button>
                )}
            </div>
            {urlError && (
                <div className="text-red-600 text-sm mt-1 px-2">Invalid URL: {urlError}</div>
            )}
            <div className="flex flex-1 space-x-6 min-h-0">
                <div className="w-1/2 flex flex-col min-h-0">
                    <InputField
                        fieldConfig={htmlFieldConfig}
                        height="flex-1"
                        className="h-full"
                        onSelect={(start, end) => {
                            setSelectionStart(start);
                            setSelectionEnd(end);
                        }}
                    />
                    <div className="flex justify-between items-center text-sm text-slate-600 mt-2">
                        <div>
                            {selectionStart !== null &&
                            selectionEnd !== null &&
                            selectionStart !== selectionEnd ? (
                                <>
                                    Selection: {selectionStart}-{selectionEnd} /{" "}
                                    {getDisplayedHtml()?.length || 0}
                                </>
                            ) : (
                                <>Character count: {getDisplayedHtml()?.length || 0}</>
                            )}
                        </div>
                    </div>
                </div>
                <div className="w-1/2 flex flex-col min-h-0">
                    {type === "submission_verification" && (
                        <SubmissionVerificationExtractedDataForm
                            submissionVerificationExtractedData={
                                submissionVerificationExtractedData
                            }
                            setSubmissionVerificationExtractedData={
                                setSubmissionVerificationExtractedData
                            }
                            readonly={mode === "display"}
                        />
                    )}
                    {type === "search" && (
                        <SearchGroundTruthForm
                            searchResultsExtractedData={searchResultsExtractedData}
                            setSearchResultsExtractedData={setSearchResultsExtractedData}
                            readonly={mode === "display"}
                        />
                    )}
                </div>
            </div>
            {mode !== "display" && (
                <div className="flex-none flex justify-end mt-4">
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="relative w-24 h-10 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                        {isSubmitting ? (
                            <LoadingSpinner className="text-white w-6 h-6" />
                        ) : mode === "create" ? (
                            "submit"
                        ) : (
                            "save"
                        )}
                    </button>
                </div>
            )}
            {isDeleteAlertOpen && (
                <AlertDialog
                    title="Confirm Deletion"
                    message="Are you sure you want to delete this training data set? This action cannot be undone."
                    onConfirm={confirmDelete}
                    onCancel={cancelDelete}
                />
            )}
            {isUpdateAlertOpen && (
                <AlertDialog
                    title="Confirm Update"
                    message="Are you sure you want to update this training data set?"
                    onConfirm={confirmUpdate}
                    onCancel={cancelUpdate}
                />
            )}
        </div>
    );
};

export default TrainingDataCreateDisplay;
